import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLoaderData, useNavigate } from 'react-router';
import type { LumiCode } from '@auto-oms/shared/types/lumiCode';
import type { CoupangFulfillmentCenter } from '@auto-oms/shared/types/fulfillmentCenter';
import { api, type ApiError } from '../lib/api';
import { pushToast } from '../lib/toast';
import { ChannelBadge } from '../components/ui/ChannelBadge';
import { PostcodeModal } from '../components/ui/PostcodeModal';
import { FulfillmentCenterPicker } from '../components/ui/FulfillmentCenterPicker';

interface LoaderData {
  items: LumiCode[];
  fulfillmentCenters: CoupangFulfillmentCenter[];
}

type ManualChannel = 'LM' | 'COUPANG_R';
const CHANNEL_VALUES: ManualChannel[] = ['LM', 'COUPANG_R'];

export async function manualOrderLoader(): Promise<LoaderData> {
  // LM, COUPANG_R 모두 사용하므로 채널 필터 없이 전체 조회 후 클라이언트에서 분리
  const [lumi, centers] = await Promise.all([
    api.get<{ items: LumiCode[] }>('/lumi-code'),
    api.get<{ items: CoupangFulfillmentCenter[] }>('/fulfillment-centers'),
  ]);
  return { items: lumi.items, fulfillmentCenters: centers.items };
}

export function ManualOrderPage() {
  const { items, fulfillmentCenters } = useLoaderData() as LoaderData;
  const nav = useNavigate();

  const [channel, setChannel] = useState<ManualChannel>('LM');
  const channelItems = useMemo(
    () => items.filter((it) => it.channel === channel),
    [items, channel],
  );

  // 각 채널의 표시명 — items[].channelName 중 첫 비어있지 않은 값을 사용. 없으면 코드.
  const channelLabels = useMemo(() => {
    const map: Record<ManualChannel, string> = { LM: 'LM', COUPANG_R: 'COUPANG_R' };
    for (const ch of CHANNEL_VALUES) {
      const named = items.find(
        (it) => it.channel === ch && it.channelName && it.channelName.trim().length > 0,
      );
      if (named?.channelName) map[ch] = named.channelName.trim();
    }
    return map;
  }, [items]);
  const channelLabel = channelLabels[channel];
  const [pCodeCh, setPCodeCh] = useState<string>(channelItems[0]?.pCodeCh ?? '');

  // 채널이 바뀌면 첫 항목 자동 선택
  const firstOfChannel = channelItems[0]?.pCodeCh ?? '';
  if (pCodeCh && !channelItems.some((it) => it.pCodeCh === pCodeCh)) {
    // 현재 선택값이 새 채널에 없으면 reset (렌더 중 setState 안전 — 동일값 가드)
    if (pCodeCh !== firstOfChannel) setPCodeCh(firstOfChannel);
  }

  // 입력 동안에는 빈 문자열을 허용해야 사용자가 "1"을 지우고 다른 숫자로 바꿀 수 있음.
  // 숫자만 허용하되 일시적 빈 상태를 막지 않는다.
  const [quantityInput, setQuantityInput] = useState<string>('1');
  const [unitPriceInput, setUnitPriceInput] = useState<string>('');
  const [totalPriceInput, setTotalPriceInput] = useState<string>('');
  const quantity = quantityInput === '' ? 0 : Number(quantityInput);
  const unitPrice = unitPriceInput === '' ? 0 : Number(unitPriceInput);
  const totalPrice = totalPriceInput === '' ? 0 : Number(totalPriceInput);
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [zonecode, setZonecode] = useState('');
  const [baseAddress, setBaseAddress] = useState('');
  const [detailAddress, setDetailAddress] = useState('');
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [deliveryType, setDeliveryType] = useState('무료');
  const [postcodeOpen, setPostcodeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // 쿠팡 로켓그로스(COUPANG_R) 채널에서만 사용하는 물류센터 선택 코드.
  // 채널이 바뀌면 비워서 LM 채널에 잘못 따라가지 않도록 한다.
  const [fulfillmentCenterCode, setFulfillmentCenterCode] = useState('');
  useEffect(() => {
    if (channel !== 'COUPANG_R') setFulfillmentCenterCode('');
  }, [channel]);

  const selectedLumi = channelItems.find((it) => it.pCodeCh === pCodeCh);
  const ea = Math.max(1, selectedLumi?.ea ?? 1);
  const effectiveQuantity = ea * quantity;

  // 채널 상품이 바뀔 때 한 번만 LumiCode.price를 판매가격에 프리필.
  // ref로 prefill 여부를 추적해야 사용자가 0/값을 지웠을 때 다시 채워넣지 않는다.
  const prefilledLumiIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!selectedLumi) return;
    if (prefilledLumiIdRef.current === selectedLumi.id) return;
    prefilledLumiIdRef.current = selectedLumi.id;
    const digits = (selectedLumi.price ?? '').replace(/[^0-9]/g, '');
    setUnitPriceInput(digits);
  }, [selectedLumi]);

  const isValid = useMemo(
    () =>
      !!pCodeCh &&
      quantity > 0 &&
      recipientName.trim().length > 0 &&
      recipientPhone.trim().length > 0 &&
      zonecode.trim().length > 0 &&
      baseAddress.trim().length > 0,
    [pCodeCh, quantity, recipientName, recipientPhone, zonecode, baseAddress],
  );

  async function onSubmit() {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      const recipientAddress = `${baseAddress} ${detailAddress}`.trim();
      const r = await api.post<{ ok: true; orderId: number; orderNumber: string }>(
        '/manual-orders',
        {
          channel,
          pCodeCh,
          quantity, // 입력 수량(서버가 ea와 곱해 최종 수량 계산)
          // 로켓그로스(COUPANG_R)는 가격 입력을 받지 않으므로 항상 0으로 저장
          unitPrice: channel === 'COUPANG_R' ? 0 : unitPrice,
          totalPrice: channel === 'COUPANG_R' ? 0 : totalPrice,
          recipientName: recipientName.trim(),
          recipientPhone: recipientPhone.trim(),
          recipientZip: zonecode.trim(),
          recipientAddress,
          deliveryMessage: deliveryMessage.trim() || null,
          deliveryType: deliveryType.trim() || '무료',
        },
      );
      pushToast(
        'success',
        `주문번호 ${r.orderNumber} 생성 완료. 최종 수량 ${effectiveQuantity}개(ea ${ea} × ${quantity}).`,
      );
      nav('/orders');
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `저장 실패: ${err.message ?? '알 수 없는 오류'}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl">
      <header className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="label-overline">자체 채널</p>
          <h1 className="mt-1 text-[28px] font-light tracking-[-0.01em] text-ink-950">수동 주문 등록</h1>
          <p className="mt-2 max-w-[58ch] text-[13px] leading-[1.6] text-ink-500">
            {channelLabels.LM} · {channelLabels.COUPANG_R} 채널의 자체 주문을 직접 등록합니다.
            등록된 주문은 수집된 주문과 함께 발주 메일에 포함됩니다.
          </p>
        </div>
        <div className="shrink-0">
          <ChannelBadge platform={channel} channelName={channelLabel} />
        </div>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
        className="space-y-4 rounded-lg border border-ink-200 bg-paper p-4 shadow-sm sm:p-5"
      >
        <Field label="채널" required>
          <div className="flex gap-2">
            {CHANNEL_VALUES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setChannel(c)}
                className={[
                  'inline-flex min-h-[44px] items-center justify-center rounded-md border px-4 text-base font-medium sm:min-h-0 sm:px-3 sm:py-1.5 sm:text-sm',
                  channel === c
                    ? 'border-coral-500 bg-coral-50 text-coral-700'
                    : 'border-ink-200 bg-paper text-ink-700 hover:bg-ink-50',
                ].join(' ')}
              >
                {channelLabels[c]}
              </button>
            ))}
          </div>
        </Field>

        {channelItems.length === 0 ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
            <p className="font-medium">{channelLabel} 채널에 등록된 상품이 없습니다.</p>
            <p className="mt-1">
              먼저{' '}
              <Link to="/lumi-code" className="underline">
                상품 매칭 코드
              </Link>{' '}
              화면에서 {channelLabel} 행을 추가해 주세요.
            </p>
          </div>
        ) : (
          <>
            <Field label="채널 상품코드" required>
              <select
                value={pCodeCh}
                onChange={(e) => setPCodeCh(e.target.value)}
                className="w-full rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
              >
                {channelItems.map((it) => (
                  <option key={it.id} value={it.pCodeCh ?? ''}>
                    {(it.pName ?? '(이름 없음)') + ' (' + (it.pCodeCh ?? '') + ')'}
                  </option>
                ))}
              </select>
              {selectedLumi && (
                <p className="mt-1 text-xs text-ink-500">
                  {selectedLumi.pDeliveryName && <>발주명: {selectedLumi.pDeliveryName} · </>}
                  단위당 ea: <span className="font-medium text-ink-700">{ea}</span>
                </p>
              )}
            </Field>

            <Field label="입력 수량" required>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={quantityInput}
                  onChange={(e) => {
                    const next = e.target.value.replace(/[^0-9]/g, '');
                    setQuantityInput(next);
                  }}
                  onBlur={() => {
                    if (quantityInput === '' || Number(quantityInput) < 1) setQuantityInput('1');
                  }}
                  className="w-32 rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
                <span className="text-sm text-ink-600">
                  × ea {ea} = 총 주문 수량{' '}
                  <span className="font-semibold text-ink-900">{effectiveQuantity}</span>개
                </span>
              </div>
            </Field>

            {channel !== 'COUPANG_R' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="판매가격">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={unitPriceInput}
                      onChange={(e) => setUnitPriceInput(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="0"
                      className="w-full rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                    />
                    <span className="shrink-0 text-sm text-ink-600">원</span>
                  </div>
                </Field>
                <Field label="금액">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={totalPriceInput}
                      onChange={(e) => setTotalPriceInput(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="0"
                      className="w-full rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                    />
                    <button
                      type="button"
                      className="btn-secondary shrink-0"
                      onClick={() =>
                        setTotalPriceInput(String(Math.max(0, unitPrice * effectiveQuantity)))
                      }
                      title="판매가격 × 총 주문 수량으로 자동 계산"
                    >
                      자동 계산
                    </button>
                  </div>
                </Field>
              </div>
            )}

            <Field label="수령인" required>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                maxLength={128}
                className="w-full rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
              />
            </Field>

            <Field label="연락처" required>
              <input
                type="text"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                placeholder="010-0000-0000"
                maxLength={32}
                className="w-full rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
              />
            </Field>

            <Field label="주소" required>
              <div className="space-y-2">
                {channel === 'COUPANG_R' && (
                  <div className="space-y-1">
                    <FulfillmentCenterPicker
                      centers={fulfillmentCenters}
                      selectedCode={fulfillmentCenterCode}
                      onSelect={(c) => {
                        setFulfillmentCenterCode(c.code);
                        setZonecode(c.parcelZip);
                        setBaseAddress(c.parcelAddress);
                        setDetailAddress('');
                        if (!recipientPhone) setRecipientPhone(c.phone);
                      }}
                    />
                    <p className="text-xs text-ink-500">
                      물류센터를 선택하면 우편번호·기본주소가 자동 입력됩니다. 직접 입력하려면 아래 "주소 검색"을 사용하세요.
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={zonecode}
                    readOnly
                    placeholder="우편번호"
                    className="w-32 rounded-md border border-ink-200 bg-ink-50 px-3 py-2.5 text-base text-ink-700 sm:py-1.5 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setPostcodeOpen(true)}
                    className="btn-secondary"
                  >
                    주소 검색
                  </button>
                </div>
                <input
                  type="text"
                  value={baseAddress}
                  readOnly
                  placeholder="기본주소 (주소 검색 후 자동 입력)"
                  className="w-full rounded-md border border-ink-200 bg-ink-50 px-3 py-2.5 text-base text-ink-700 sm:py-1.5 sm:text-sm"
                />
                <input
                  type="text"
                  value={detailAddress}
                  onChange={(e) => setDetailAddress(e.target.value)}
                  placeholder="상세주소 (예: 101동 202호)"
                  maxLength={400}
                  className="w-full rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
                />
              </div>
            </Field>

            <Field label="배송메시지">
              <input
                type="text"
                value={deliveryMessage}
                onChange={(e) => setDeliveryMessage(e.target.value)}
                placeholder="예: 부재시 경비실에 맡겨주세요"
                maxLength={255}
                className="w-full rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
              />
            </Field>

            <Field label="배송방식">
              <input
                type="text"
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value)}
                placeholder="무료 / 착불 / 선결제 등"
                maxLength={20}
                className="w-40 rounded-md border border-ink-200 bg-paper px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-coral-500 sm:py-1.5 sm:text-sm"
              />
              <p className="mt-1 text-xs text-ink-500">기본값은 "무료"입니다.</p>
            </Field>

            <div className="flex justify-end gap-2 border-t border-ink-200 pt-4">
              <button type="button" className="btn-secondary" onClick={() => nav('/orders')}>
                취소
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={!isValid || saving}
                title={!isValid ? '필수 항목을 모두 입력해 주세요.' : undefined}
              >
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
          </>
        )}
      </form>

      <PostcodeModal
        open={postcodeOpen}
        onClose={() => setPostcodeOpen(false)}
        onComplete={(data) => {
          setZonecode(data.zonecode);
          setBaseAddress(data.address);
          setFulfillmentCenterCode('');
        }}
      />
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-ink-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
